import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { Box, Button, Skeleton } from '@mui/material';
import { useQuery } from 'react-query';
import {
  formatFundCnpj,
  FundCarousel,
  FundCarouselCard,
} from 'src/components/FundCarousel';
import {
  getMetaCarteirasFicFidcsCarousel,
  MetaCarteirasFicFidcsCarouselItem,
  registerMetaCarteirasFicFidcsCarouselHit,
} from 'src/services/meta-carteiras-fic-fidcs';
import { convertValue } from 'src/utils/number';

function isValidCarouselItem(item: MetaCarteirasFicFidcsCarouselItem) {
  return Boolean(item.operationId?.trim() && item.name?.trim());
}

function RecommendedFundsCarouselSkeleton() {
  return (
    <FundCarousel title="Monitore os FICFIDCs" icon={<MonitorHeartIcon />}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={index}
          data-testid="recommended-funds-carousel-skeleton-card"
          sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Skeleton width="30%" height={28} />
          <Skeleton width="90%" height={32} />
          <Skeleton width="70%" height={24} />
          <Skeleton width="45%" height={40} />
        </Box>
      ))}
    </FundCarousel>
  );
}

export default function RecommendedFundsCarousel() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['meta-carteiras-fic-fidcs-carousel'],
    queryFn: () => getMetaCarteirasFicFidcsCarousel(),
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <RecommendedFundsCarouselSkeleton />;
  }

  const items = data?.data?.filter(isValidCarouselItem) || [];

  if (isError || items.length === 0) {
    return null;
  }

  const handleView = (operationId: string) => {
    window.open(
      `/monitor/wallet/view/${operationId}`,
      '_blank',
      'noopener,noreferrer',
    );
    void registerMetaCarteirasFicFidcsCarouselHit(operationId).catch(() => {
      // Hit registration is fire-and-forget and must not block navigation.
    });
  };

  return (
    <FundCarousel
      title="Monitore os FICFIDCs"
      icon={<MonitorHeartIcon />}
      infinite={true}
      autoPlay={true}
      autoPlayInterval={3000}>
      {items.map((item) => (
        <FundCarouselCard key={item.operationId}>
          <FundCarouselCard.Header>
            {item.badge && (
              <FundCarouselCard.Badge>{item.badge}</FundCarouselCard.Badge>
            )}

            <FundCarouselCard.Title>{item.name}</FundCarouselCard.Title>

            <FundCarouselCard.Divider />
          </FundCarouselCard.Header>

          <FundCarouselCard.Body>
            {item.cnpj && (
              <FundCarouselCard.Field>
                <FundCarouselCard.Field.Label>
                  CNPJ
                </FundCarouselCard.Field.Label>
                <FundCarouselCard.Field.Value>
                  {formatFundCnpj(item.cnpj)}
                </FundCarouselCard.Field.Value>
              </FundCarouselCard.Field>
            )}
            {typeof item.pl === 'number' && (
              <FundCarouselCard.Field>
                <FundCarouselCard.Field.Label>
                  PL Atual
                </FundCarouselCard.Field.Label>
                <FundCarouselCard.Field.Value
                  color="text.primary"
                  fontSize={12}
                  lineHeight={1.1}>
                  {convertValue(item.pl)}
                </FundCarouselCard.Field.Value>
              </FundCarouselCard.Field>
            )}
          </FundCarouselCard.Body>

          <FundCarouselCard.Action>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleView(item.operationId)}
              aria-label={`Ver ${item.name}`}>
              Ver
            </Button>
          </FundCarouselCard.Action>
        </FundCarouselCard>
      ))}
    </FundCarousel>
  );
}
